type PriceLike =
  | {
      currencyIso?: string;
      formattedValue?: string;
      value?: number;
    }
  | undefined;

/** ISO 3166-1 alpha-2 region, e.g. "be". */
const REGION_PATTERN = /^[a-z]{2}$/i;

/**
 * Locale prices are formatted in, e.g. "fr-BE" for /be/fr.
 *
 * Falls back to the bare language when the route's country is not a valid
 * region subtag (e.g. a global site), so formatting never throws.
 */
export function getPriceLocale(language: string, country?: string): string {
  const normalizedLanguage = language.replaceAll("_", "-");
  const candidates =
    country && REGION_PATTERN.test(country)
      ? [`${normalizedLanguage}-${country.toUpperCase()}`, normalizedLanguage]
      : [normalizedLanguage];

  for (const candidate of candidates) {
    try {
      return Intl.getCanonicalLocales(candidate)[0] ?? candidate;
    } catch {
      // Not a valid BCP 47 tag, try the next candidate.
    }
  }

  return "en";
}

export function formatCurrency(
  value: number,
  currencyIso: string | undefined,
  locale: string
): string {
  if (!currencyIso) {
    return String(value);
  }

  try {
    return new Intl.NumberFormat(locale, {
      currency: currencyIso,
      style: "currency",
    }).format(value);
  } catch (_error) {
    return `${value} ${currencyIso}`;
  }
}

/**
 * Formats a SAP price for the shop's locale.
 *
 * SAP's own `formattedValue` is only a fallback: the search and the product
 * detail endpoints format the same price differently, so cards built from
 * either would otherwise show mixed separators on one page.
 */
export function formatPrice(
  price: PriceLike,
  locale: string
): string | undefined {
  if (!price) {
    return;
  }

  if (typeof price.value === "number" && price.currencyIso) {
    return formatCurrency(price.value, price.currencyIso, locale);
  }

  return price.formattedValue;
}
